import { format } from '@visx/vendor/d3-format';
import { timeFormat, utcFormat } from '@visx/vendor/d3-time-format';
import { flow } from 'lodash';
import { cond } from 'lodash/fp';

import { UTCDate, utc } from '@blockworks/platform';

import { intervalBounds } from '../../../utils';

const replaceGiga = (formattedNumber: string) => formattedNumber?.replace?.('G', 'B');

const stubTrue = (_: any) => true;

const createDynamicFormatter = (unit: string = '') => {
    return flow(
        cond([
            [(number: number) => number > 100000 || number < -100000, format(unit + ',.0f')],
            [(number: number) => number > 1 || number < -1, format(unit + ',.2~f')],
            [
                (number: number) => (number <= 0.000001 && number > 0) || (number >= -0.000001 && number < 0),
                format(unit + ',.2~e'),
            ],
            [(number: number) => number <= 1 && number >= -1, format(unit + '~f')],
            [stubTrue, format(unit + ',.0f')], // Fallback
        ]),
        replaceGiga,
    );
};

const createDynamicTickFormatter = (unit: string = '') => {
    return flow(
        cond([
            [(number: number) => number > 1 || number < -1, format(unit + ',.2~s')],
            [(number: number) => number >= 0.01 || number <= -0.01, format(unit + ',.2~')],
            [(number: number) => number >= 0.001 || number <= -0.001 || number === 0, format(unit + ',.3~')],
            [stubTrue, format(unit + ',.2~e')],
        ]),
        replaceGiga,
    );
};

const percentTickFormatter = flow(
    cond([
        [(number: number) => number > 10 || number < -10, format('.0~%')],
        [(number: number) => number > 0.1 || number < -0.1, format('.1~%')],
        [(number: number) => number > 0.01 || number < -0.01, format('.2~%')],
        [(number: number) => number >= -0.001 && number <= 0.001, format('.3~%')],
        [stubTrue, format('.4~p')], // Fallback
    ]),
    replaceGiga,
);

const percentFormatter = flow(
    cond([
        [(number: number) => number > 10 || number < -10, format('.0~%')],
        [(number: number) => number > 0.1 || number < -0.1, format('.2~%')],
        [(number: number) => number > 0.01 || number < -0.01, format('.2~%')],
        [(number: number) => number > 0.000001 || number < -0.000001, format('.4~%')],
        [(number: number) => number === 0, format('.0~p')],
        [
            (number: number) => number >= -0.000001 && number <= 0.000001,
            flow(
                (number: number) => (number > 0 ? 1 : -1) * 0.000001,
                format('.4~%'),
                (value: string) => `Less than ${value}`,
            ),
        ],
    ]),
    replaceGiga,
);

const localeFormat = {
    utc: {
        full: utcFormat('%a %b %e %X %Y'),
        quarter: utcFormat('Q%q %Y'),
        short: utcFormat('%b %-d'),
        long: utcFormat('%b %-d, %Y'),
    },
    local: {
        full: timeFormat('%a %b %e %X %Y'),
        quarter: timeFormat('Q%q %Y'),
        short: timeFormat('%b %-d'),
        long: timeFormat('%b %-d, %Y'),
    },
};

const formatFullDate = utcFormat('%a %b %e %X %Y');
const formatQuarter = utcFormat('Q%q %Y');
const formatShortDate = utcFormat('%b %-d');
const formatLongDate = utcFormat('%b %-d, %Y');

const formats = {
    smallnumber: {
        tooltip: createDynamicFormatter(),
        tick: createDynamicTickFormatter(),
    },
    number: {
        tooltip: createDynamicFormatter(),
        tick: createDynamicTickFormatter(),
    },
    usdShort: {
        tooltip: createDynamicFormatter('$'),
        tick: createDynamicTickFormatter('$'),
    },
    usd: {
        tooltip: createDynamicFormatter('$'),
        tick: createDynamicTickFormatter('$'),
    },
    percent: {
        tooltip: percentFormatter,
        tick: percentTickFormatter,
    },
};

const timeFormats = {
    years: {
        tick: utcFormat('%Y'),
        tooltip: (date: Date) => {
            const bounds = intervalBounds.years;
            const utcStart = bounds.start(new UTCDate(date), { in: utc });
            const utcEnd = bounds.end(new UTCDate(date), { in: utc });
            return `${formatShortDate(utcStart)} - ${formatLongDate(utcEnd)}`;
        },
    },
    quarters: {
        tick: utcFormat('Q%q %y'),
        tooltip: (date: Date) => {
            return formatQuarter(date);
        },
    },
    months: {
        tick: utcFormat("%b '%y"),
        tooltip: (date: Date) => {
            const bounds = intervalBounds.months;
            const utcStart = bounds.start(new UTCDate(date), { in: utc });
            const utcEnd = bounds.end(new UTCDate(date), { in: utc });
            return `${formatShortDate(utcStart)} - ${formatLongDate(utcEnd)}`;
        },
    },
    weeks: {
        tick: utcFormat("%b '%y"),
        tooltip: (date: Date) => {
            const bounds = intervalBounds.weeks;

            const utcStart = bounds.start(new UTCDate(date), { in: utc });
            const utcEnd = bounds.end(new UTCDate(date), { in: utc });
            return `${formatShortDate(utcStart)} - ${formatLongDate(utcEnd)}`;
        },
    },
    days: {
        tick: utcFormat('%b %-d'),
        tooltip: formatLongDate,
    },
    hours: {
        tick: utcFormat('%-m/%-d'),
        tooltip: formatFullDate,
    },
};

const formatTick = (date: Date, interval?: string) => {
    if (timeFormats[interval as keyof typeof timeFormats]) {
        return timeFormats[interval as keyof typeof timeFormats].tick(date);
    }
    return timeFormats.days.tick(date);
};

const formatOptions = {
    number: [
        {
            label: 'USD',
            value: 'usd',
        },
        { label: 'Number', value: 'number' },
        { label: 'Percent', value: 'percent' },
    ],
    numberLegacy: [
        {
            label: 'USD',
            value: 'usd',
        },
        {
            label: 'USD Shorthand', // @DEPRECATED
            value: 'usdShort',
        },
        { label: 'Small Number', value: 'smallnumber' }, // @DEPRECATED
        { label: 'Numeric', value: 'number' },
        { label: 'Percent', value: 'percent' },
    ],
    time: [
        { label: 'Yearly', value: 'yearly' },
        { label: 'Quarterly', value: 'quarterly' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Daily', value: 'daily' },
        { label: 'Hourly', value: 'hourly' },
    ],
};

export { formatOptions, formats, formatTick, localeFormat, replaceGiga, timeFormats };
